import pointService from 'live/services/charts/point'
import Utils from 'live/lib/utils'
import { CustomPlotlineElement, ExtendedPlotline } from './index'
import { tooltipPositioner } from '../positioners'
import { Widget } from '../service'
import { MarkersData } from '../../api'
import { removeDuplicates } from '../../utils'

const LABEL_HEIGHT = 20

type TooltipLabels = {
    name: string
    color: string
    value: number
    unit: string
    show?: boolean
    index?: number
}

export interface CustomPlotlineTooltip extends CustomPlotlineElement {
    labels: Highcharts.SVGElement[]
    flip: boolean | null
    expandedWidth: number | null
}

export default function Tooltip(plotline: ExtendedPlotline): CustomPlotlineElement {
    if (plotline.options?.id === 'placeholder') {
        return ({ update: () => null, destroy: () => null } as unknown) as CustomPlotlineElement
    }

    const chart: Widget['chart'] = plotline.axis.chart
    const isInverted = chart.inverted
    const marker = plotline.linePath
    const { value } = plotline.options

    const tooltip = chart.renderer
        .g('markers-tooltip')
        .attr({ translateX: marker.start.x + 3, translateY: marker.start.y, zIndex: 99 })
        .on('mouseover', function () {
            plotline?.menuButton?.attr?.({ visibility: 'visible' })
        })
        .on('mouseout', function () {
            plotline?.menuButton?.attr?.({ visibility: 'hidden' })
        })
        .add() as CustomPlotlineTooltip

    tooltip.update = function (plotline, hide) {
        const { tooltip, focus } = plotline

        if (tooltip.labels?.length > 0) {
            tooltip.labels.forEach(item => item.destroy())
        }

        const widgetId = plotline.axis.chart.widgetId
        const widgetPoints = plotline.options.data.widgets.find(a => a.widgetId === widgetId)
        const labels = createLabelsForTooltip(
            plotline.axis.chart,
            tooltip,
            value,
            plotline.options.data.markerType,
            widgetPoints
        )

        plotline.options.data.widgets[0] = {
            points: labels,
            widgetId: widgetId || plotline.options.data.widgets[0].widgetId,
            widgetName: plotline.axis.chart.widgetName
        }

        const position = tooltipPositioner(
            plotline.linePath,
            this.getBBox(),
            plotline.axis.chart.plotBox,
            isInverted as boolean,
            this.expandedWidth as number
        )

        this.attr({ ...position, visibility: hide || !focus ? 'hidden' : 'visible' })

        tooltip.flip = position.flip

        return position
    }

    tooltip.destroy = function (this: CustomPlotlineTooltip) {
        ;(this.labels || []).forEach(item => item.destroy())
        this.labels = []
        this.flip = null
        this.update = () => null

        return Object.getPrototypeOf(this).destroy.call(this)
    }

    const widgetPoints = plotline.options.data.widgets.find(a => a.widgetId === chart.widgetId)

    const labels = createLabelsForTooltip(chart, tooltip, value, plotline.options.data.markerType, widgetPoints)

    if (labels.length === 0) {
        console.warn('[Markers] Tooltip labels are empty', labels)
        console.warn('[Markers] x: ', value)
    }

    plotline.options.data.widgets = [{ points: labels, widgetId: chart.widgetId, widgetName: chart.widgetName }]

    return tooltip
}

function createLabelsForTooltip(
    chart: Widget['chart'],
    tooltip: CustomPlotlineTooltip,
    value: number,
    showValues: 'STATIC' | 'PLOT_VALUE' = 'PLOT_VALUE',
    widgetPoints?: MarkersData['widgets'][0],
    merge?: boolean
): TooltipLabels[] {
    let labels: TooltipLabels[] = chart.series
        // The series needs to be visible and NOT wrapped.
        // As discussed in https://gitlab.intelie.com/intelie/live/-/issues/1531, the wrap
        // series feature duplicates the same series under a different name. Here
        // we are filtering out those duplicate series.
        .map(s => s.visible && !hasWrappedSeries(s.name) && getClosestPoint(s, value))
        .filter(Boolean)
        .map(item => ({
            value: item.y,
            name: item.series.name,
            color: item.series.color,
            unit: item.series.unit || item.series.uom || guessUnit(item),
            show: true
        }))

    if (showValues === 'STATIC') {
        labels = widgetPoints?.points?.length ? widgetPoints.points : labels
    }

    // Use a deterministic order
    if (labels.every(a => a.index != null)) {
        labels = labels.sort((a, b) => a.index - b.index)
    } else {
        labels = labels.sort((a, b) => a.name.localeCompare(b.name))
    }

    tooltip.labels = []

    removeDuplicates(labels).forEach((label, index) => {
        // The number 0 in the line below is the amount of margin.
        makeLabelFor(label, tooltip, index * LABEL_HEIGHT + index * 0, chart)
    })

    return labels
}

function getClosestPoint(
    series: Highcharts.Series & { yData?: number[]; xData?: number[] },
    value: number
): Highcharts.Point {
    if (series.points?.length > 0) return pointService.getClosestPoint(series.points, { x: value })

    return null
}

function makeLabelFor(
    labelData: TooltipLabels,
    tooltip: CustomPlotlineTooltip,
    y: number,
    chart: Highcharts.Chart
): void {
    const curve = chart.renderer.g('marker-curve').attr({ translateY: y })

    const icon = chart.renderer.circle({ cx: 10, cy: LABEL_HEIGHT / 2, r: 4, fill: labelData.color })

    const valuestr = Utils.isNumber(labelData.value) ? Utils.formatNumber(labelData.value, 0, 2) : '??'

    const value = chart.renderer.text(valuestr, 18, 14).attr({
        class: 'value',
        'font-size': '13px',
        fill: 'var(--gray-900)',
        'font-weight': '600',
        'font-family': 'SourceSansPro-Regular'
    })

    const unit = chart.renderer
        .text(labelData.unit ?? '', 20, 14)
        .attr({
            class: 'unit',
            'font-size': '13px',
            fill: 'var(--gray-900)',
            'font-weight': 'bold',
            'font-family': 'SourceSansPro-Regular'
        })
        .css({ 'transform-origin': '25px 16px' })

    const name = chart.renderer
        .text(labelData.name + ': ', 20, 14)
        .attr({ class: 'name' })
        .css({
            'transform-origin': '20px 16px',
            'font-size': '13px',
            fill: 'var(--gray-900)',
            'font-family': 'SourceSansPro-Regular'
        })

    icon.add(curve)
    name.add(curve)
    value.add(curve)
    unit.add(curve)

    curve.add(tooltip)

    // padding + circle diameter + text width + borders + padding
    const collapsedTooltipWidth = 10 + 8 + value.getBBox().width + 2 + 5
    const expandedTooltipWidth = collapsedTooltipWidth + name.getBBox().width + unit.getBBox().width
    const expandedValueLeft = expandedTooltipWidth - unit.getBBox().width - collapsedTooltipWidth + 3
    const expandedUnitLeft = expandedValueLeft + value.getBBox().width + 0

    const rect = chart.renderer
        .rect({ height: LABEL_HEIGHT, width: collapsedTooltipWidth, zIndex: -1, rx: 2 })
        .css({ stroke: labelData.color })
        .add(curve)

    curve.on('mouseover', () => {
        rect.attr({ width: expandedTooltipWidth })
        name.css({ transform: 'scaleX(1)' })
        value.css({ transform: 'translateX(' + expandedValueLeft + 'px)' })
        unit.css({ transform: 'translateX(' + expandedUnitLeft + 'px) scaleX(1)' })
    })

    curve.on('mouseout', () => {
        rect.attr({ width: collapsedTooltipWidth })
        name.css({ transform: 'scaleX(0)' })
        value.css({ transform: 'translateX(0px)' })
        unit.css({ transform: 'translateX(0px) scaleX(0)' })
    })

    tooltip.labels.push(curve)

    tooltip.expandedWidth = Math.max(tooltip.expandedWidth || 0, expandedTooltipWidth)
}

function guessUnit(item: Highcharts.Point & { formattedValue?: string }): string {
    if (typeof item.series.userOptions?.dataLabels?.formatter === 'function') {
        try {
            const value = item.series.userOptions.dataLabels.formatter.call({ y: 0 })
            const [, ...unit] = value.split(' ')

            if (unit.length > 0) return unit.join(' ')
        } catch (ex) {
            // default to formatted value
        }
    }

    const [value, unit] = (item.formattedValue || '').split(' ')

    // no unit
    if (value == null || value === '') return ''

    if (unit != null && unit !== '') return unit

    return ''
}

// https://gitlab.intelie.com/intelie/live/-/issues/1531
function hasWrappedSeries(text: string): boolean {
    return text.includes('(below min)') || text.includes('(above max)') || !!text.match((/\((nega|posi)tiveFill\)/gi))
}
